import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from "flowbite-react";
import { request } from "../../../api/config";
import Layout from "../../layout.tsx/app";
import { Button } from "../../../components/buttons";
import BaseModal from "../../../components/modal/base";
import Table from "../../../components/tables/base";
import { useClient } from "../../../stores/partner/client";
import { useAlert } from "../../../stores/alert";
import { parseDate } from "../../../helper/date";

const ClientDetailPage = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const [modalReset, setModalReset] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const { client, setClient } = useClient();
  const { clientId } = useParams();
  const { setMessage } = useAlert();

  const getClient = async () => {
    setLoading(true);
    try {
      const { data } = await request.get(`/client/${clientId}`);
      setClient(data.data);
    } catch {
      setMessage("Gagal memuat data klien", "error");
    }
    setLoading(false);
  };

  const closeModal = () => {
    setModalReset(false);
    setPassword("");
    setPasswordConfirmation("");
    setError(null);
  };

  const handleResetPassword = async () => {
    if (password.length < 6) {
      setError("Password minimal 6 karakter");
      return;
    }
    if (password !== passwordConfirmation) {
      setError("Konfirmasi password tidak sama");
      return;
    }

    setLoadingSubmit(true);
    try {
      await request.post(`/client/${clientId}/reset-password`, {
        password,
        password_confirmation: passwordConfirmation,
      });
      setMessage("Password klien berhasil direset", "success");
      closeModal();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? "Oops, something went wrong!"
      );
    }
    setLoadingSubmit(false);
  };

  useEffect(() => {
    setClient(null);
    getClient();
  }, [clientId]);

  return (
    <Layout
      withPageTitle
      title={client?.name ?? "Detail Klien"}
      pageTitleContent={
        <Button
          onClick={() => setModalReset(true)}
          className="px-6"
          variant="danger"
          disabled={loading || !client}
        >
          Reset Password
        </Button>
      }
    >
      {loading || !client ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <strong>Informasi Klien</strong>
            <div className="py-2 grid grid-cols-1 gap-2">
              <div>
                <small>NIK</small> <br />
                <p>{client.nik ?? "-"}</p>
              </div>
              <div>
                <small>Nama</small> <br />
                <p>{client.name}</p>
              </div>
              <div>
                <small>Email</small> <br />
                <p>{client.email}</p>
              </div>
              <div>
                <small>No. HP</small> <br />
                <p>{client.phone ?? "-"}</p>
              </div>
              <div>
                <small>Jenis Kelamin</small> <br />
                <p>{client.gender ?? "-"}</p>
              </div>
              <div>
                <small>Zona Waktu</small> <br />
                <p>{client.timezone ?? "-"}</p>
              </div>
              <div>
                <small>Status</small> <br />
                <p>{client.is_active ? "Aktif" : "Tidak aktif"}</p>
              </div>
              <div>
                <small>Perusahaan Utama</small> <br />
                <p>{client.default_company?.name ?? "-"}</p>
              </div>
              <div>
                <small>Tanggal Mendaftar</small> <br />
                <p>{client.created_at ? parseDate(client.created_at) : "-"}</p>
              </div>
            </div>
          </div>
          <div className="md:col-span-3">
            <span className="font-bold">Perusahaan</span>
            <Table className="mt-2">
              <Table.Thead>
                <Table.Th>#</Table.Th>
                <Table.Th>Nama Perusahaan</Table.Th>
                <Table.Th>Tgl Join</Table.Th>
              </Table.Thead>
              <Table.Tbody>
                {client.companies?.length ? (
                  client.companies.map((item, key) => (
                    <Table.Tr key={item.id}>
                      <Table.Td>
                        <>{key + 1}</>
                      </Table.Td>
                      <Table.Td>{item.name}</Table.Td>
                      <Table.Td>
                        <>{item.joined_at ? parseDate(item.joined_at) : "-"}</>
                      </Table.Td>
                    </Table.Tr>
                  ))
                ) : (
                  <Table.Tr>
                    <Table.Td cols={3} className="text-center py-3">
                      Belum tergabung di perusahaan manapun
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </div>
        </div>
      )}

      <BaseModal isOpen={modalReset} close={closeModal}>
        <h1 className="text-lg">
          Reset password <strong>{client?.name}</strong>
        </h1>
        <span>
          Semua sesi login klien akan dikeluarkan setelah password diganti.
        </span>
        <div className="mt-4 grid gap-2">
          <input
            type="password"
            className="rounded-lg border-gray-300"
            placeholder="Password baru"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <input
            type="password"
            className="rounded-lg border-gray-300"
            placeholder="Konfirmasi password baru"
            value={passwordConfirmation}
            onChange={(e) => setPasswordConfirmation(e.target.value)}
          />
          {error && <small className="text-red-600">{error}</small>}
        </div>
        <div className="mt-4 flex justify-end">
          <div className="flex gap-2">
            <Button onClick={closeModal} className="px-4" variant="danger">
              Batal
            </Button>
            <Button onClick={handleResetPassword} className="px-16">
              {loadingSubmit ? <Spinner /> : "Simpan"}
            </Button>
          </div>
        </div>
      </BaseModal>
    </Layout>
  );
};

export default ClientDetailPage;
